// Drop-in replacement for `toast` from sonner that runs every user-visible
// string through `simplifyMaybe`. When the active reading style is
// "simplified", any registered phrase in SIMPLIFIED_COPY is swapped for its
// plain-language equivalent. Unknown strings pass through unchanged so
// partial coverage stays safe.
//
// Usage:
//   import { toast } from "sonner";
//   toast.error("Couldn't save your hunt");
//
// All of sonner's call shapes are supported: toast(msg), toast.error(msg),
// toast.success(msg, { description }), etc. The `description` field is
// simplified too. ReactNode messages are passed through untouched.
import { toast as sonnerToast, type ExternalToast } from "sonner";
import { simplifyMaybe } from "@/lib/simplifiedCopy";

type Msg = string | React.ReactNode;

function s(msg: Msg): Msg {
  return typeof msg === "string" ? simplifyMaybe(msg) : msg;
}
function opts(o?: ExternalToast): ExternalToast | undefined {
  if (!o) return o;
  if (typeof o.description === "string") {
    return { ...o, description: simplifyMaybe(o.description) };
  }
  return o;
}

function callable(msg: Msg, o?: ExternalToast) {
  return sonnerToast(s(msg) as string, opts(o));
}

export const toast = Object.assign(callable, {
  success: (msg: Msg, o?: ExternalToast) => sonnerToast.success(s(msg) as string, opts(o)),
  error:   (msg: Msg, o?: ExternalToast) => sonnerToast.error(s(msg) as string, opts(o)),
  info:    (msg: Msg, o?: ExternalToast) => sonnerToast.info(s(msg) as string, opts(o)),
  warning: (msg: Msg, o?: ExternalToast) => sonnerToast.warning(s(msg) as string, opts(o)),
  message: (msg: Msg, o?: ExternalToast) => sonnerToast.message(s(msg) as string, opts(o)),
  loading: (msg: Msg, o?: ExternalToast) => sonnerToast.loading(s(msg) as string, opts(o)),
  dismiss: sonnerToast.dismiss,
  promise:  sonnerToast.promise,
  custom:   sonnerToast.custom,
});

export type { ExternalToast };